import {ApolloQueryResult} from '@apollo/client';
import {BaseUnitType, Term, Unit, unitFromTerms, UnitType} from '@benchling/units';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import {difference, flatten} from 'underscore';

import notifications from 'notifications';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {GENERIC_SHORT_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import SectionTabs from 'ui-library/SectionTabs';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
import {
  createOnboardingUnitTypesAndUnits,
  createUnitType,
  CreateUnitTypeParams,
  OnboardingUnitTypeAndUnitsCreateParams,
  OnboardingUnitTypeAndUnitsData,
} from 'units/UnitDictionaryDAO';
import {UnitTypesQueryUnitTypeNode} from 'units/UnitsConstants';
import UnitTypeForm, {formatFormula} from 'units/UnitTypeForm';
import {isServerRequestError} from 'util/Net';
import omitKey from 'util/omitKey';

import {getUnitsDependencyDataStructures} from './onboarding-wizard/OnboardingWizardUtil';
import UnitsOnboardingWizardUnitsScreen from './onboarding-wizard/UnitsOnboardingWizardUnitsScreen';
import UnitsOnboardingWizardUnitTypesScreen from './onboarding-wizard/UnitsOnboardingWizardUnitTypesScreen';
import {HydratedUnit} from './util';

export const UNIT_TYPE_CREATION_MODAL_KEY = 'unit-type-creation';

export interface UnitTypeCreationModalProps {
  onRequestClose: () => void;
  refetch: () => Promise<ApolloQueryResult<UnitTypesQuery>>;
  defaultSiUnitsData: Array<OnboardingUnitTypeAndUnitsData>;
  defaultPdUnitsData: Array<OnboardingUnitTypeAndUnitsData>;
  existingUnitTypes: Array<UnitTypesQueryUnitTypeNode>;
}

export type OnboardingUnitOption = Unit & {mustBeSelected: boolean};
export type OnboardingUnitTypeOption = UnitType & {mustBeSelected: boolean};

export enum UnitTypeCreationModalTab {
  SI_UNITS = 'SI_UNITS',
  PD_UNITS = 'PD_UNITS',
  CREATE_NEW = 'CREATE_NEW',
}

export const UNIT_TYPE_CREATION_MODAL_TAB_TO_READABLE: Record<UnitTypeCreationModalTab, string> = {
  [UnitTypeCreationModalTab.SI_UNITS]: 'SI Units',
  [UnitTypeCreationModalTab.PD_UNITS]: 'PD Units',
  [UnitTypeCreationModalTab.CREATE_NEW]: 'Custom',
};

function getFilteredDefaultUnitsData(
  defaultUnitsData: Array<OnboardingUnitTypeAndUnitsData>,
  existingUnitTypes: Array<UnitTypesQueryUnitTypeNode>
): Array<OnboardingUnitTypeAndUnitsData> {
  const {unitToDependentUnitTypes} = getUnitsDependencyDataStructures(defaultUnitsData);

  const existingBaseUnitSymbolToExisingUnitTypeName = new Map<string, string>();
  for (const unitType of existingUnitTypes) {
    existingBaseUnitSymbolToExisingUnitTypeName.set(unitType.baseUnit.symbol, unitType.name);
  }
  // Filter out any data where only one of the base unit symbol or unit type name is already in the dictionary.
  const filteredDefaultUnitsData = defaultUnitsData.filter((data) => {
    const {unitType: defaultUnitType, baseUnitSymbol: defaultBaseUnitSymbol} = data;
    // If the default data's base unit symbol is already in the dictionary, make sure the unit type name is the same.
    const existingUnitTypeName = existingBaseUnitSymbolToExisingUnitTypeName.get(defaultBaseUnitSymbol);
    if (existingUnitTypeName != null) {
      return existingUnitTypeName === defaultUnitType.name;
    }
    // If the unit type name is already in the dictionary, make sure the base unit symbol is the same.
    const existingBaseUnitSymbol = existingUnitTypes.find(
      (existingUnitType) => defaultUnitType.name === existingUnitType.name
    )?.baseUnit.symbol;
    return existingBaseUnitSymbol == null || existingBaseUnitSymbol === defaultBaseUnitSymbol;
  });

  // Filter out any unit types that depend on filtered out units
  const unfilteredDefaultUnitSymbols = flatten(
    defaultUnitsData.map((data) => data.units.map((unit) => unit.symbol))
  );
  const filteredDefaultUnitSymbols = flatten(
    filteredDefaultUnitsData.map((data) => data.units.map((unit) => unit.symbol))
  );
  const filteredOutUnits = difference(unfilteredDefaultUnitSymbols, filteredDefaultUnitSymbols);
  const filteredOutUnitTypeNames = new Set(
    flatten(filteredOutUnits.map((unitSymbol) => unitToDependentUnitTypes[unitSymbol]))
  );
  return filteredDefaultUnitsData.filter((data) => !filteredOutUnitTypeNames.has(data.unitType.name));
}

async function submitManuallyCreatedUnitType(
  name: string,
  simplifiedTerms: Array<Term<BaseUnitType>>,
  baseUnitName: string,
  baseUnitSymbol: string,
  setErrorMessage: (errorMessage: string) => void,
  onRequestClose: () => void,
  combinedTerms: Array<{exponent: number; value: HydratedUnit}>,
  refetch: () => Promise<ApolloQueryResult<UnitTypesQuery>>
): Promise<void> {
  const data: CreateUnitTypeParams = {
    name: name.trim(),
    terms: simplifiedTerms,
    baseUnit: {name: baseUnitName.trim(), symbol: baseUnitSymbol.trim()},
    formula: formatFormula(combinedTerms),
  };
  try {
    setErrorMessage('');
    await createUnitType(data);
    notifications.success(`Unit type ${name} successfully created`);
    onRequestClose();
    refetch();
  } catch (e) {
    if (isServerRequestError(e)) {
      setErrorMessage(notifications.getFriendlyError(e) ?? GENERIC_SHORT_ERROR_MESSAGE);
      return;
    }
    throw e;
  }
}

async function submitPresetUnitTypes(
  filteredDefaultUnitsData: Array<OnboardingUnitTypeAndUnitsData>,
  selectedUnitTypeNames: Array<string>,
  selectedUnitSymbols: Array<string>,
  refetch: () => Promise<ApolloQueryResult<UnitTypesQuery>>,
  onRequestClose: () => void
): Promise<void> {
  const creates = [];
  for (const unitTypeAndUnitsData of filteredDefaultUnitsData) {
    const {unitType, units} = unitTypeAndUnitsData;
    const unitTypeName = unitType.name;
    if (!selectedUnitTypeNames.includes(unitTypeName)) {
      continue;
    }
    const baseUnit = units.find((unit) => unit.symbol === unitTypeAndUnitsData.baseUnitSymbol)!;
    const unitsToCreate = units
      .filter((unit) => selectedUnitSymbols.includes(unit.symbol))
      .filter((unit) => unit.symbol !== baseUnit.symbol)
      .map((unit) => ({
        name: unit.name,
        symbol: unit.symbol,
        aliases: unit.aliases,
        conversionFactor: unit.conversionFactor,
      }));
    const create: OnboardingUnitTypeAndUnitsCreateParams = {
      unitType: {
        ...omitKey(unitType, 'icon'),
        name: unitTypeName,
        baseUnit: {
          name: baseUnit.name,
          symbol: baseUnit.symbol,
          aliases: baseUnit.aliases,
          siName: baseUnit.siName,
        },
        formula: unitTypeAndUnitsData.formula,
      },
      units: unitsToCreate,
      rank: unitTypeAndUnitsData.rank,
    };
    creates.push(create);
  }
  LoadingScreen.show();
  await createOnboardingUnitTypesAndUnits(creates);
  await refetch();
  onRequestClose();
  LoadingScreen.hide();
  notifications.success('Successfully added units to dictionary');
}

export default function UnitTypeCreationModal({
  onRequestClose,
  refetch,
  defaultSiUnitsData,
  defaultPdUnitsData,
  existingUnitTypes,
}: UnitTypeCreationModalProps): JSX.Element {
  const [activeTab, setActiveTab] = React.useState<UnitTypeCreationModalTab>(
    UnitTypeCreationModalTab.SI_UNITS
  );

  // Preset units data
  const hasExistingUnitTypes = existingUnitTypes.length > 0;
  const filteredDefaultSiUnitsData = getFilteredDefaultUnitsData(defaultSiUnitsData, existingUnitTypes);
  const filteredDefaultPdUnitsData = getFilteredDefaultUnitsData(defaultPdUnitsData, existingUnitTypes);
  const allFilteredDefaultUnitsData = filteredDefaultSiUnitsData.concat(filteredDefaultPdUnitsData);
  const existingUnitTypeNames = existingUnitTypes.map((unitType) => unitType.name);
  const existingDefaultUnitTypeNames = allFilteredDefaultUnitsData
    .filter((data) => existingUnitTypeNames.includes(data.unitType.name))
    .map((data) => data.unitType.name);
  const [selectedUnitTypeNames, setSelectedUnitTypeNames] = useState<Array<string>>(
    existingDefaultUnitTypeNames
  );
  const [currentUnitTypeName, setCurrentUnitTypeName] = useState<string | null>(null);

  const existingUnitSymbols = flatten(
    existingUnitTypes.map((unitType) => unitType.units.map((unit) => unit.symbol))
  );
  const defaultSelectedUnitSymbols: Array<string> = flatten(
    allFilteredDefaultUnitsData.map((data) =>
      data.units
        .filter((unit) => unit.symbol === data.baseUnitSymbol || existingUnitSymbols.includes(unit.symbol))
        .map((unit) => unit.symbol)
    )
  );
  const [selectedUnitSymbols, setSelectedUnitSymbols] = useState<Array<string>>(defaultSelectedUnitSymbols);
  const unitsDependencyDataStructures = getUnitsDependencyDataStructures(allFilteredDefaultUnitsData);

  // Manual creation data
  const [numeratorUnits, setNumeratorUnits] = useState<Array<HydratedUnit>>([]);
  const [denominatorUnits, setDenominatorUnits] = useState<Array<HydratedUnit>>([]);
  const [name, setName] = useState('');
  const [baseUnitName, setBaseUnitName] = useState('');
  const [baseUnitSymbol, setBaseUnitSymbol] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const combinedTerms = [
    ...numeratorUnits.map((unit) => ({value: unit, exponent: 1})),
    ...denominatorUnits.map((unit) => ({value: unit, exponent: -1})),
  ];
  const simplifiedTerms = unitFromTerms('', '', combinedTerms).unitType.terms;

  const canSaveManuallyCreatedUnitType =
    name.trim().length > 0 && baseUnitName.trim().length > 0 && baseUnitSymbol.trim().length > 0;

  const selectedUnitsAndExistingUnitsDifferent = selectedUnitSymbols.some(
    (symbol) =>
      !existingUnitSymbols.includes(symbol) &&
      selectedUnitTypeNames.includes(unitsDependencyDataStructures.unitSymbolToUnitTypeName[symbol])
  );

  // Shared modal components
  const buttons: Array<ModalButton> = [];
  if (activeTab === UnitTypeCreationModalTab.SI_UNITS || activeTab === UnitTypeCreationModalTab.PD_UNITS) {
    if (hasExistingUnitTypes && currentUnitTypeName == null) {
      buttons.push({
        text: 'Cancel',
        onClick: onRequestClose,
        cancel: true,
      });
    }
    buttons.push({
      text: 'Save',
      disabled: currentUnitTypeName != null || !selectedUnitsAndExistingUnitsDifferent,
      onClick: () => {
        submitPresetUnitTypes(
          allFilteredDefaultUnitsData,
          selectedUnitTypeNames,
          selectedUnitSymbols,
          refetch,
          onRequestClose
        );
      },
      submit: true,
    });
  } else {
    buttons.push({
      text: 'Cancel',
      cancel: true,
    });
    buttons.push({
      text: 'Add unit type',
      disabled: !canSaveManuallyCreatedUnitType,
      submit: true,
      onClick: () => {
        submitManuallyCreatedUnitType(
          name,
          simplifiedTerms,
          baseUnitName,
          baseUnitSymbol,
          setErrorMessage,
          onRequestClose,
          combinedTerms,
          refetch
        );
      },
    });
  }

  const tabs = (
    <SectionTabs
      tabs={[
        {
          text: 'SI Units',
          ariaControls: `${UnitTypeCreationModalTab.SI_UNITS}_PANEL`,
          id: `${UnitTypeCreationModalTab.SI_UNITS}_TAB`,
          isActive: activeTab === UnitTypeCreationModalTab.SI_UNITS,
          handleClick: () => {
            setActiveTab(UnitTypeCreationModalTab.SI_UNITS);
          },
        },
        {
          text: 'PD Units',
          ariaControls: `${UnitTypeCreationModalTab.PD_UNITS}_PANEL`,
          id: `${UnitTypeCreationModalTab.PD_UNITS}_TAB`,
          isActive: activeTab === UnitTypeCreationModalTab.PD_UNITS,
          handleClick: () => {
            setActiveTab(UnitTypeCreationModalTab.PD_UNITS);
          },
        },
        {
          text: 'Custom',
          ariaControls: `${UnitTypeCreationModalTab.CREATE_NEW}_PANEL`,
          id: `${UnitTypeCreationModalTab.CREATE_NEW}_TAB`,
          isActive: activeTab === UnitTypeCreationModalTab.CREATE_NEW,
          handleClick: () => {
            setActiveTab(UnitTypeCreationModalTab.CREATE_NEW);
          },
        },
      ]}
    />
  );

  return (
    <Modal
      title="Add unit type"
      buttons={buttons}
      onRequestClose={onRequestClose}
      scrollable={true}
      expandHeight={true}
      bodyPadded={false}
      closeable={hasExistingUnitTypes}
      large={activeTab === UnitTypeCreationModalTab.CREATE_NEW}
    >
      <div className={css(styles.bodyContainer)}>
        {activeTab === UnitTypeCreationModalTab.SI_UNITS ? (
          currentUnitTypeName == null ? (
            <div>
              {tabs}
              <div
                aria-labelledby={`${UnitTypeCreationModalTab.SI_UNITS}_TAB`}
                id={`${UnitTypeCreationModalTab.SI_UNITS}_PANEL`}
                role="tabpanel"
              >
                <UnitsOnboardingWizardUnitTypesScreen
                  defaultUnitsData={filteredDefaultSiUnitsData}
                  selectedUnitTypeNames={selectedUnitTypeNames}
                  selectedUnitSymbols={selectedUnitSymbols}
                  onSetSelectedUnitTypeNames={setSelectedUnitTypeNames}
                  onSetSelectedUnitSymbols={setSelectedUnitSymbols}
                  onSetCurrentUnitTypeName={setCurrentUnitTypeName}
                  unitsDependencyDataStructures={unitsDependencyDataStructures}
                  existingUnitTypes={existingUnitTypes}
                />
              </div>
            </div>
          ) : (
            <UnitsOnboardingWizardUnitsScreen
              unitTypeData={
                filteredDefaultSiUnitsData.find((data) => data.unitType.name === currentUnitTypeName)!
              }
              selectedUnitTypeNames={selectedUnitTypeNames}
              selectedUnitSymbols={selectedUnitSymbols}
              onSetSelectedUnitTypeNames={setSelectedUnitTypeNames}
              onSetSelectedUnitSymbols={setSelectedUnitSymbols}
              onSetCurrentUnitTypeName={setCurrentUnitTypeName}
              unitsDependencyDataStructures={unitsDependencyDataStructures}
              existingUnitTypes={existingUnitTypes}
            />
          )
        ) : activeTab === UnitTypeCreationModalTab.PD_UNITS ? (
          currentUnitTypeName == null ? (
            <div>
              {tabs}
              <div
                aria-labelledby={`${UnitTypeCreationModalTab.PD_UNITS}_TAB`}
                id={`${UnitTypeCreationModalTab.PD_UNITS}_PANEL`}
                role="tabpanel"
              >
                <UnitsOnboardingWizardUnitTypesScreen
                  defaultUnitsData={filteredDefaultPdUnitsData}
                  selectedUnitTypeNames={selectedUnitTypeNames}
                  selectedUnitSymbols={selectedUnitSymbols}
                  onSetSelectedUnitTypeNames={setSelectedUnitTypeNames}
                  onSetSelectedUnitSymbols={setSelectedUnitSymbols}
                  onSetCurrentUnitTypeName={setCurrentUnitTypeName}
                  unitsDependencyDataStructures={unitsDependencyDataStructures}
                  existingUnitTypes={existingUnitTypes}
                />
              </div>
            </div>
          ) : (
            <UnitsOnboardingWizardUnitsScreen
              unitTypeData={
                filteredDefaultPdUnitsData.find((data) => data.unitType.name === currentUnitTypeName)!
              }
              selectedUnitTypeNames={selectedUnitTypeNames}
              selectedUnitSymbols={selectedUnitSymbols}
              onSetSelectedUnitTypeNames={setSelectedUnitTypeNames}
              onSetSelectedUnitSymbols={setSelectedUnitSymbols}
              onSetCurrentUnitTypeName={setCurrentUnitTypeName}
              unitsDependencyDataStructures={unitsDependencyDataStructures}
              existingUnitTypes={existingUnitTypes}
            />
          )
        ) : (
          <div>
            {tabs}
            <div
              aria-labelledby={`${UnitTypeCreationModalTab.CREATE_NEW}_TAB`}
              className={css(styles.manualCreationScreen)}
              id={`${UnitTypeCreationModalTab.CREATE_NEW}_PANEL`}
              role="tabpanel"
            >
              <UnitTypeForm
                name={name}
                onNameChange={setName}
                simplifiedTerms={simplifiedTerms}
                numeratorUnits={numeratorUnits}
                onSetNumeratorUnits={setNumeratorUnits}
                denominatorUnits={denominatorUnits}
                onSetDenominatorUnits={setDenominatorUnits}
                baseUnitName={baseUnitName}
                onSetBaseUnitName={setBaseUnitName}
                baseUnitSymbol={baseUnitSymbol}
                onSetBaseUnitSymbol={setBaseUnitSymbol}
                errorMessage={errorMessage}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'scroll',
  },
  bodyContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  text: {
    ...FONT_PROXIMA_12,
  },
  textContainer: {
    marginLeft: 16,
    marginRight: 16,
    marginTop: 12,
  },
  manualCreationScreen: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
  },
});
